import { useCallback, useEffect, useState } from "react";
import { Archive, Download, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FREQ = { off: "Solo quando lo avvio io", daily: "Ogni notte", weekly: "Ogni domenica notte" };
const TONE = { completato: "green", completato_con_avvisi: "amber", errore: "red", in_corso: "blue" };
const LABEL = { completato: "Completato", completato_con_avvisi: "Completato con avvisi", errore: "Errore", in_corso: "In corso" };
const fmt = (d) => (d ? new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");
const mb = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : "");

const RunRow = ({ r }) => {
  const get = async () => {
    try {
      const res = await api.get(`/admin/tenant-backup/download/${r.id}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = r.filename; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <li className="flex flex-wrap items-center gap-2 py-2 text-xs" data-testid={`backup-run-${r.id}`}>
      <StatusBadge tone={TONE[r.status]}>{LABEL[r.status]}</StatusBadge>
      <span className="text-slate-600">{fmt(r.started_at)} · {r.target === "webdav" ? "Cartella" : "Da scaricare"} · {r.trigger}</span>
      {r.size ? <span className="text-slate-400">{mb(r.size)}</span> : null}
      {r.error && <span className="w-full text-red-600">{r.error}</span>}
      {r.downloadable && <Button size="sm" variant="outline" className="ml-auto h-7 gap-1" onClick={get} data-testid={`backup-download-${r.id}`}><Download className="h-3.5 w-3.5" />Scarica</Button>}
    </li>
  );
};

export const TenantBackupPanel = () => {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/tenant-backup").then((r) => {
    setData(r.data);
    setF((cur) => cur || { url: r.data.settings.url || "", username: r.data.settings.username || "", password: "", frequency: r.data.settings.frequency });
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = data?.runs?.some((r) => r.status === "in_corso");
  useEffect(() => {
    if (!running) { return undefined; }
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);
  if (!data || !f) return null;
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/tenant-backup", { ...f, url: f.url.trim() || null, password: f.password || null }); toast.success("Impostazioni backup salvate e cartella verificata"); setF({ ...f, password: "" }); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const run = async (target) => {
    try { await api.post("/admin/tenant-backup/run", { target }); toast.success(target === "download" ? "Preparazione del backup avviata" : "Backup verso la cartella avviato"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const s = data.settings;
  return (
    <Panel className="p-5" data-testid="tenant-backup-panel">
      <SectionLabel icon={Archive}>Backup dei dati</SectionLabel>
      <p className="mt-1 text-xs text-slate-500">Un file ZIP con tutti i dati (clienti, offerte, ordini, fatture, contratti, attività…) e i documenti caricati. Le password non sono incluse. Nella cartella restano gli ultimi {s.keep} backup.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Cartella WebDAV (es. Aruba Drive, Nextcloud, NAS)</Label>
          <Input value={f.url} placeholder="https://…/remote.php/dav/files/utente/Backup/" onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="backup-input-url" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Utente</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="backup-input-username" /></div>
        <div className="space-y-1.5"><Label className="text-xs">{s.has_password ? "Password (vuoto = invariata)" : "Password"}</Label>
          <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="backup-input-password" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Backup automatico nella cartella</Label>
          <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}>
            <SelectTrigger data-testid="backup-select-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(FREQ).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`backup-frequency-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} disabled={busy} data-testid="backup-save">{busy ? "Verifica cartella…" : "Salva e verifica"}</Button>
        <Button variant="outline" className="gap-1.5" onClick={() => run("webdav")} disabled={running || !s.url} data-testid="backup-run-webdav"><Play className="h-3.5 w-3.5" />Backup nella cartella ora</Button>
        <Button variant="outline" className="gap-1.5" onClick={() => run("download")} disabled={running} data-testid="backup-run-download"><Download className="h-3.5 w-3.5" />Prepara backup da scaricare</Button>
      </div>
      {data.runs.length > 0 && <ul className="mt-4 divide-y divide-line border-t border-line" data-testid="backup-runs">{data.runs.map((r) => <RunRow key={r.id} r={r} />)}</ul>}
    </Panel>
  );
};
